/**
 * Why a detected date matters (sc-11466, Lake Stevens R2-03).
 *
 * The server tags every detected DATE (server/services/redaction/date_context.py):
 * DOB, BANKING, PHI, FERPA, PERSONNEL, PERSONAL (another private date about a person,
 * Sentinel only), or NONE when it is no protected kind of date.
 * With Sentinel the detection model types each date from the page context; the
 * text detectors use keyword rules. A NONE date is stored declined: it is not
 * suggested for redaction.
 */
import type { EntitySuggestion, RedactionReason } from '../types';

export const NO_PROTECTED_CONTEXT = 'NONE';

interface DateContextInfo {
  label: string;
  /** What the tag means, for the tooltip. */
  description: string;
  /** A tenant reason whose title or citation matches this becomes the default reason. */
  reasonMatch: RegExp;
}

export const DATE_CONTEXTS: Record<string, DateContextInfo> = {
  DOB: {
    label: 'DOB',
    description: 'Date of birth',
    // RCW 42.56.230 on its own (no subsection), FOIA Exemption 6, or a date-of-birth reason.
    reasonMatch: /date of birth|birth ?date|\bdob\b|exemption 6|\(b\)\s*\(6\)|42\.56\.230(?!\s*\()/i,
  },
  BANKING: {
    label: 'Banking',
    description: 'Card data: a card expiry or other financial-card date',
    reasonMatch: /credit card|debit card|card number|financial|banking|42\.56\.230\s*\(5\)/i,
  },
  PHI: {
    label: 'PHI',
    description: "Health information: the date of a person's health event",
    reasonMatch: /hipaa|\bphi\b|health|medical/i,
  },
  FERPA: {
    label: 'FERPA',
    description: 'Student record: an enrollment, graduation or attendance date',
    reasonMatch: /ferpa|student|education record/i,
  },
  PERSONNEL: {
    label: 'Personnel',
    description: 'Personnel record: a hire, separation or start date',
    reasonMatch: /personnel|employee|employment|42\.56\.230\s*\(3\)/i,
  },
  PERSONAL: {
    label: 'Personal',
    description:
      'Personal: a private date about a person (death, arrest or booking, ID issue or expiry, benefits, protection order, marriage or divorce)',
    // RCW 42.56.230 on its own, FOIA Exemption 6 / 7(C), or a personal-information reason.
    reasonMatch: /personal information|exemption 6|exemption 7\s*\(?c\)?|\(b\)\s*\((6|7)\)|42\.56\.230(?!\s*\()/i,
  },
};

export const NOT_SUGGESTED_HINT =
  'Not suggested: not a birth, card, health, student, personnel or other personal date';

/** The tag's short label ("DOB", "Banking", ...), or null for no tag / NONE. */
export function dateContextLabel(tag: string | null | undefined): string | null {
  if (!tag || tag === NO_PROTECTED_CONTEXT) return null;
  return DATE_CONTEXTS[tag]?.label ?? tag.charAt(0) + tag.slice(1).toLowerCase();
}

/** One tag for several occurrences of a value: a protected tag beats NONE (one DOB
 *  occurrence makes the value a DOB); among protected tags the most frequent wins. */
export function bestDateContext(tags: Array<string | null | undefined>): string | null {
  const counts = new Map<string, number>();
  for (const tag of tags) if (tag) counts.set(tag, (counts.get(tag) ?? 0) + 1);
  const order = Object.keys(DATE_CONTEXTS);
  const protectedTags = [...counts.keys()].filter((t) => t !== NO_PROTECTED_CONTEXT);
  if (protectedTags.length === 0) return counts.size ? NO_PROTECTED_CONTEXT : null;
  return protectedTags.sort((a, b) => {
    const byCount = (counts.get(b) ?? 0) - (counts.get(a) ?? 0);
    if (byCount) return byCount;
    const ia = order.indexOf(a), ib = order.indexOf(b);
    return (ia < 0 ? order.length : ia) - (ib < 0 ? order.length : ib);
  })[0];
}

/** A results-panel group's tag. */
export function groupDateContext(suggestions: EntitySuggestion[]): string | null {
  return bestDateContext(suggestions.map((s) => s.dateContext));
}

/** The machine declined this date (no protected context) and no one has decided otherwise. */
export function isNotSuggestedDate(s: EntitySuggestion): boolean {
  return s.dateContext === NO_PROTECTED_CONTEXT && s.status === 'rejected' && !s.dictionaryAction;
}

/** The tenant reason that fits the tag's basis (title or citation), if any. Never creates one. */
export function defaultReasonForDateContext(
  tag: string | null | undefined,
  reasons: RedactionReason[],
): RedactionReason | undefined {
  const info = tag ? DATE_CONTEXTS[tag] : undefined;
  if (!info) return undefined;
  return [...reasons]
    .sort((a, b) => a.title.localeCompare(b.title))
    .find((r) => info.reasonMatch.test(r.title) || info.reasonMatch.test(r.citation ?? ''));
}
